import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoiceDollar,
  faTriangleExclamation,
  faCircleCheck,
  faTag,
  faChevronRight,
  faTimes,
  faRotate,
  faSearch,
  faFileInvoice,
  faMoneyBillWave,
  faArrowRight,
  faCalendarDays,
  faChartLine,
  faArrowTrendUp,
  faArrowTrendDown,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  BarChart, Bar,
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../services/api';
import InvoicePreview from './InvoicePreview';
import './Billing.css';

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = (val) =>
  val != null
    ? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const fmtK = (val) => {
  const n = Number(val);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const isOverdue = (inv) =>
  !inv.isPaid && inv.dueDate && new Date(inv.dueDate) < new Date(new Date().toDateString());

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color, loading, onClick }) {
  return (
    <button
      className={`billing-kpi-card ${onClick ? 'billing-kpi-card--clickable' : ''}`}
      style={{ '--kpi-accent': color }}
      onClick={onClick}
      disabled={!onClick}
    >
      <div className="billing-kpi-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="billing-kpi-body">
        <div className="billing-kpi-value">
          {loading ? <span className="billing-skeleton" /> : (value ?? '—')}
        </div>
        <div className="billing-kpi-label">{label}</div>
        {sub && !loading && <div className="billing-kpi-sub">{sub}</div>}
      </div>
      {onClick && <FontAwesomeIcon icon={faArrowRight} className="billing-kpi-arrow" />}
    </button>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ invoice }) {
  if (invoice.isPaid) return <span className="billing-badge billing-badge--paid">Paid</span>;
  if (isOverdue(invoice)) return <span className="billing-badge billing-badge--overdue">Overdue</span>;
  return <span className="billing-badge billing-badge--open">Open</span>;
}

function CreditBadge({ memo }) {
  if (Number(memo.remainingCredit) === 0)
    return <span className="billing-badge billing-badge--paid">Applied</span>;
  return <span className="billing-badge billing-badge--credit">Available</span>;
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="billing-chart-tip">
      <div className="billing-chart-tip-label">{fmtMonth(label)}</div>
      {payload.map((p) => (
        <div key={p.name} className="billing-chart-tip-row">
          <span className="billing-chart-tip-dot" style={{ background: p.color }} />
          <span>{p.name}:</span>
          <strong>{fmtK(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────
function DetailDrawer({ title, onClose, loading, children }) {
  return (
    <>
      <div className="billing-drawer-overlay" onClick={onClose} />
      <aside className="billing-drawer">
        <div className="billing-drawer-header">
          <h2 className="billing-drawer-title">{title}</h2>
          <button className="billing-drawer-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="billing-drawer-body">
          {loading ? (
            <div className="billing-drawer-loading">
              <div className="billing-spinner" />
              <span>Loading…</span>
            </div>
          ) : children}
        </div>
      </aside>
    </>
  );
}

function LineItemsTable({ items }) {
  if (!items?.length) return <p className="billing-empty-sub">No line items found.</p>;
  return (
    <table className="billing-lines">
      <thead>
        <tr>
          <th>#</th>
          <th>Description</th>
          <th>Item</th>
          <th className="billing-lines-num">Qty</th>
          <th className="billing-lines-num">Unit Price</th>
          <th className="billing-lines-num">Amount</th>
        </tr>
      </thead>
      <tbody>
        {items.map((li) => (
          <tr key={li.lineNum}>
            <td className="billing-lines-num">{li.lineNum}</td>
            <td>{li.description || '—'}</td>
            <td>{li.itemName || '—'}</td>
            <td className="billing-lines-num">{li.quantity != null ? Number(li.quantity).toLocaleString() : '—'}</td>
            <td className="billing-lines-num">{li.unitPrice != null ? fmt(li.unitPrice) : '—'}</td>
            <td className="billing-lines-num">{fmt(li.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Year-over-Year stat box ───────────────────────────────────────────────────
function YoyCard({ label, current, prior, format = fmtK }) {
  const delta = current != null && prior != null && prior !== 0
    ? ((current - prior) / prior) * 100
    : null;
  const up = delta != null && delta >= 0;
  return (
    <div className="billing-yoy-card">
      <div className="billing-yoy-label">{label}</div>
      <div className="billing-yoy-values">
        <div className="billing-yoy-current">
          <span className="billing-yoy-year">{new Date().getFullYear()}</span>
          <span className="billing-yoy-amount">{current != null ? format(current) : '—'}</span>
        </div>
        <div className="billing-yoy-current billing-yoy-prior">
          <span className="billing-yoy-year">{new Date().getFullYear() - 1}</span>
          <span className="billing-yoy-amount">{prior != null ? format(prior) : '—'}</span>
        </div>
      </div>
      {delta != null && (
        <div className={`billing-yoy-delta ${up ? 'billing-yoy-delta--up' : 'billing-yoy-delta--down'}`}>
          <FontAwesomeIcon icon={up ? faArrowTrendUp : faArrowTrendDown} />
          {Math.abs(delta).toFixed(1)}% vs prior year
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Billing() {
  const [tab, setTab] = useState('invoices');
  const [kpis, setKpis] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);

  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [invStatus, setInvStatus] = useState('all');
  const [invSearch, setInvSearch] = useState('');
  const [invFrom, setInvFrom] = useState('');
  const [invTo, setInvTo] = useState('');

  // Credit memo state
  const [credits, setCredits] = useState([]);
  const [cmLoading, setCmLoading] = useState(true);
  const [cmSearch, setCmSearch] = useState('');
  const [cmFrom, setCmFrom] = useState('');
  const [cmTo, setCmTo] = useState('');

  // Reports state
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsFetched, setReportsFetched] = useState(false);

  // Invoice full preview
  const [invoicePreview, setInvoicePreview] = useState(null); // { invoice, lineItems }
  const [invPreviewLoading, setInvPreviewLoading] = useState(false);

  // Credit memo drawer
  const [selectedCm, setSelectedCm] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchKpis = useCallback(async () => {
    setKpiLoading(true);
    try {
      const res = await api.get('/api/billing/kpis');
      setKpis(res.data);
    } catch {
      setKpis(null);
    } finally {
      setKpiLoading(false);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setInvLoading(true);
    try {
      const params = {};
      if (invStatus !== 'all') params.status = invStatus;
      if (invFrom) params.fromDate = invFrom;
      if (invTo)   params.toDate   = invTo;
      if (invSearch.trim()) params.search = invSearch.trim();
      const res = await api.get('/api/billing/invoices', { params });
      setInvoices(res.data.invoices || []);
    } catch {
      setInvoices([]);
    } finally {
      setInvLoading(false);
    }
  }, [invStatus, invFrom, invTo, invSearch]);

  const fetchCredits = useCallback(async () => {
    setCmLoading(true);
    try {
      const params = {};
      if (cmFrom) params.fromDate = cmFrom;
      if (cmTo)   params.toDate   = cmTo;
      if (cmSearch.trim()) params.search = cmSearch.trim();
      const res = await api.get('/api/billing/credit-memos', { params });
      setCredits(res.data.creditMemos || []);
    } catch {
      setCredits([]);
    } finally {
      setCmLoading(false);
    }
  }, [cmFrom, cmTo, cmSearch]);

  const fetchReports = useCallback(async () => {
    if (reportsFetched) return;
    setReportsLoading(true);
    try {
      const res = await api.get('/api/billing/reports');
      setReports(res.data);
    } catch {
      setReports(null);
    } finally {
      setReportsLoading(false);
      setReportsFetched(true);
    }
  }, [reportsFetched]);

  useEffect(() => { fetchKpis(); }, [fetchKpis]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);
  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  useEffect(() => {
    if (tab === 'reports') fetchReports();
  }, [tab, fetchReports]);

  const openInvoice = async (inv) => {
    setInvPreviewLoading(true);
    setInvoicePreview(null); // keep null until data is ready so usePDF gets full data on first render
    try {
      const res = await api.get(`/api/billing/invoices/${inv.id}`);
      setInvoicePreview({ invoice: res.data.invoice, lineItems: res.data.lineItems });
    } catch {
      setInvoicePreview(null);
    } finally {
      setInvPreviewLoading(false);
    }
  };

  const openCreditMemo = async (cm) => {
    setSelectedCm(cm);
    setSelectedInv(null);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/api/billing/credit-memos/${cm.id}`);
      setDetailData(res.data);
    } catch {
      setDetailData({ error: true });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setSelectedCm(null);
    setDetailData(null);
  };

  // Trim to last 12 months for charts
  const chartData = reports?.monthly?.slice(-12).map(r => ({
    ...r,
    label: r.month,
  })) ?? [];

  const momData = (() => {
    if (!reports?.monthly?.length) return null;
    const months = reports.monthly;
    if (months.length < 2) return null;
    const cur = months[months.length - 1];
    const prev = months[months.length - 2];
    return { cur, prev };
  })();

  return (
    <div className="billing">

      {/* ── KPI Row ─────────────────────────────────────────────────────────── */}
      <div className="billing-kpis">
        <KpiCard
          icon={faFileInvoiceDollar}
          label="Total Outstanding"
          value={kpis ? fmt(kpis.totalOutstanding) : null}
          sub={kpis ? `${kpis.openCount} open invoice${kpis.openCount !== 1 ? 's' : ''}` : null}
          color="#3b82f6"
          loading={kpiLoading}
          onClick={() => { setTab('invoices'); setInvStatus('open'); }}
        />
        <KpiCard
          icon={faTriangleExclamation}
          label="Overdue"
          value={kpis ? fmt(kpis.totalOverdue) : null}
          sub={kpis?.totalOverdue > 0 ? 'Past due — action required' : 'No overdue invoices'}
          color={kpis?.totalOverdue > 0 ? '#ef4444' : '#10b981'}
          loading={kpiLoading}
          onClick={kpis?.totalOverdue > 0 ? () => { setTab('invoices'); setInvStatus('overdue'); } : undefined}
        />
        <KpiCard
          icon={faMoneyBillWave}
          label="Credit Memos"
          value={kpis ? fmt(kpis.availableCredits) : null}
          sub="On account"
          color="#10b981"
          loading={kpiLoading}
          onClick={() => setTab('credits')}
        />
        <KpiCard
          icon={faCircleCheck}
          label="Paid Invoices"
          value={kpis ? kpis.paidCount.toLocaleString() : null}
          sub={kpis ? `${kpis.totalInvoices} total` : null}
          color="#8b5cf6"
          loading={kpiLoading}
          onClick={() => { setTab('invoices'); setInvStatus('paid'); }}
        />
      </div>

      {kpis?.lastSynced && (
        <p className="billing-sync-note">
          <FontAwesomeIcon icon={faRotate} />
          Last sync: {new Date(kpis.lastSynced).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
          })}
        </p>
      )}

      {/* ── Tab bar ─────────────────────────────────────────────────────────── */}
      <div className="billing-tabs" role="tablist">
        <button role="tab" className={`billing-tab ${tab === 'invoices' ? 'billing-tab--active' : ''}`}
          onClick={() => setTab('invoices')} aria-selected={tab === 'invoices'}>
          <FontAwesomeIcon icon={faFileInvoice} />
          Invoices
          {kpis?.openCount > 0 && <span className="billing-tab-badge">{kpis.openCount}</span>}
        </button>
        <button role="tab" className={`billing-tab ${tab === 'credits' ? 'billing-tab--active' : ''}`}
          onClick={() => setTab('credits')} aria-selected={tab === 'credits'}>
          <FontAwesomeIcon icon={faTag} />
          Credit Memos
        </button>
        <button role="tab" className={`billing-tab ${tab === 'reports' ? 'billing-tab--active' : ''}`}
          onClick={() => setTab('reports')} aria-selected={tab === 'reports'}>
          <FontAwesomeIcon icon={faChartLine} />
          Reports
        </button>
      </div>

      {/* ══════════════════════════ INVOICES TAB ══════════════════════════════ */}
      {tab === 'invoices' && (
        <div className="billing-panel">
          <div className="billing-filters">
            <div className="billing-search-wrap">
              <FontAwesomeIcon icon={faSearch} className="billing-search-icon" />
              <input className="billing-input billing-search" placeholder="Invoice # or PO #"
                value={invSearch} onChange={(e) => setInvSearch(e.target.value)} />
            </div>
            <select className="billing-select" value={invStatus} onChange={(e) => setInvStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="overdue">Overdue</option>
              <option value="paid">Paid</option>
            </select>
            <div className="billing-filter-dates">
              <FontAwesomeIcon icon={faCalendarDays} className="billing-date-icon" />
              <input type="date" className="billing-input billing-date" value={invFrom}
                onChange={(e) => setInvFrom(e.target.value)} />
              <span className="billing-date-sep">–</span>
              <input type="date" className="billing-input billing-date" value={invTo}
                onChange={(e) => setInvTo(e.target.value)} />
            </div>
            {(invStatus !== 'all' || invSearch || invFrom || invTo) && (
              <button className="billing-clear-btn" onClick={() => { setInvStatus('all'); setInvSearch(''); setInvFrom(''); setInvTo(''); }}>
                ✕ Clear
              </button>
            )}
            <span className="billing-count">
              {!invLoading && `${invoices.length.toLocaleString()} record${invoices.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Invoice #</th><th>Date</th><th>Due Date</th><th>PO #</th>
                  <th className="billing-col-num">Total</th>
                  <th className="billing-col-num">Balance</th>
                  <th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {invLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="billing-row-skeleton">
                      {Array.from({ length: 8 }).map((__, j) => <td key={j}><span className="billing-skeleton" /></td>)}
                    </tr>
                  ))
                ) : invoices.length === 0 ? (
                  <tr><td colSpan={8} className="billing-empty">
                    <FontAwesomeIcon icon={faFileInvoice} />
                    <span>No invoices found</span>
                  </td></tr>
                ) : invoices.map((inv) => (
                  <tr key={inv.id}
                    className="billing-row"
                    onClick={() => openInvoice(inv)}>
                    <td className="billing-doc-num">{inv.docNumber || '—'}</td>
                    <td>{fmtDate(inv.txnDate)}</td>
                    <td className={isOverdue(inv) ? 'billing-overdue-date' : ''}>{fmtDate(inv.dueDate)}</td>
                    <td className="billing-muted">{inv.poNumber || '—'}</td>
                    <td className="billing-col-num">{fmt(inv.totalAmt)}</td>
                    <td className={`billing-col-num ${!inv.isPaid && Number(inv.balance) > 0 ? 'billing-balance-due' : ''}`}>
                      {fmt(inv.balance)}
                    </td>
                    <td><StatusBadge invoice={inv} /></td>
                    <td className="billing-col-action"><FontAwesomeIcon icon={faChevronRight} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════ CREDITS TAB ═══════════════════════════════ */}
      {tab === 'credits' && (
        <div className="billing-panel">
          <div className="billing-filters">
            <div className="billing-search-wrap">
              <FontAwesomeIcon icon={faSearch} className="billing-search-icon" />
              <input className="billing-input billing-search" placeholder="Credit memo #"
                value={cmSearch} onChange={(e) => setCmSearch(e.target.value)} />
            </div>
            <div className="billing-filter-dates">
              <FontAwesomeIcon icon={faCalendarDays} className="billing-date-icon" />
              <input type="date" className="billing-input billing-date" value={cmFrom}
                onChange={(e) => setCmFrom(e.target.value)} />
              <span className="billing-date-sep">–</span>
              <input type="date" className="billing-input billing-date" value={cmTo}
                onChange={(e) => setCmTo(e.target.value)} />
            </div>
            {(cmSearch || cmFrom || cmTo) && (
              <button className="billing-clear-btn" onClick={() => { setCmSearch(''); setCmFrom(''); setCmTo(''); }}>
                ✕ Clear
              </button>
            )}
            <span className="billing-count">
              {!cmLoading && `${credits.length.toLocaleString()} record${credits.length !== 1 ? 's' : ''}`}
            </span>
          </div>

          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>Credit Memo #</th><th>Date</th>
                  <th className="billing-col-num">Total Credit</th>
                  <th className="billing-col-num">Remaining</th>
                  <th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {cmLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="billing-row-skeleton">
                      {Array.from({ length: 6 }).map((__, j) => <td key={j}><span className="billing-skeleton" /></td>)}
                    </tr>
                  ))
                ) : credits.length === 0 ? (
                  <tr><td colSpan={6} className="billing-empty">
                    <FontAwesomeIcon icon={faTag} />
                    <span>No credit memos found</span>
                  </td></tr>
                ) : credits.map((cm) => (
                  <tr key={cm.id}
                    className={`billing-row ${selectedCm?.id === cm.id ? 'billing-row--selected' : ''}`}
                    onClick={() => openCreditMemo(cm)}>
                    <td className="billing-doc-num">{cm.docNumber || '—'}</td>
                    <td>{fmtDate(cm.txnDate)}</td>
                    <td className="billing-col-num">{fmt(cm.totalAmt)}</td>
                    <td className={`billing-col-num ${Number(cm.remainingCredit) > 0 ? 'billing-credit-avail' : ''}`}>
                      {fmt(cm.remainingCredit)}
                    </td>
                    <td><CreditBadge memo={cm} /></td>
                    <td className="billing-col-action"><FontAwesomeIcon icon={faChevronRight} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════ REPORTS TAB ═══════════════════════════════ */}
      {tab === 'reports' && (
        <div className="billing-panel billing-reports">
          {reportsLoading ? (
            <div className="billing-reports-loading">
              <div className="billing-spinner" />
              <span>Loading report data…</span>
            </div>
          ) : !reports ? (
            <div className="billing-empty" style={{ padding: 56 }}>
              <FontAwesomeIcon icon={faChartLine} />
              <span>No report data available</span>
            </div>
          ) : (
            <>
              {/* ── Year-over-Year ─────────────────────────────────────────── */}
              <div className="billing-reports-section">
                <h3 className="billing-reports-heading">Year-over-Year</h3>
                <div className="billing-yoy-row">
                  <YoyCard label="Total Invoiced"
                    current={reports.yearTotals.current?.invoiced}
                    prior={reports.yearTotals.prior?.invoiced} />
                  <YoyCard label="Total Paid"
                    current={reports.yearTotals.current?.paid}
                    prior={reports.yearTotals.prior?.paid} />
                  <YoyCard label="Outstanding"
                    current={reports.yearTotals.current?.outstanding}
                    prior={reports.yearTotals.prior?.outstanding} />
                  <YoyCard label="Invoice Count"
                    current={reports.yearTotals.current?.invoiceCount}
                    prior={reports.yearTotals.prior?.invoiceCount}
                    format={(v) => v?.toLocaleString()} />
                </div>
              </div>

              {/* ── Month-over-Month ───────────────────────────────────────── */}
              {momData && (
                <div className="billing-reports-section">
                  <h3 className="billing-reports-heading">Month-over-Month
                    <span className="billing-reports-sub">
                      {fmtMonth(momData.prev.month)} → {fmtMonth(momData.cur.month)}
                    </span>
                  </h3>
                  <div className="billing-yoy-row">
                    {[
                      { label: 'Invoiced', cur: momData.cur.invoiced, prev: momData.prev.invoiced },
                      { label: 'Paid',     cur: momData.cur.paid,     prev: momData.prev.paid },
                      { label: 'Outstanding', cur: momData.cur.outstanding, prev: momData.prev.outstanding },
                      { label: 'Invoices',    cur: momData.cur.invoiceCount, prev: momData.prev.invoiceCount,
                        fmt: (v) => v?.toLocaleString() },
                    ].map(({ label, cur, prev, fmt: f }) => {
                      const delta = prev && prev !== 0 ? ((cur - prev) / prev) * 100 : null;
                      const up = delta != null && delta >= 0;
                      return (
                        <div key={label} className="billing-yoy-card">
                          <div className="billing-yoy-label">{label}</div>
                          <div className="billing-yoy-values">
                            <div className="billing-yoy-current">
                              <span className="billing-yoy-year">{fmtMonth(momData.cur.month)}</span>
                              <span className="billing-yoy-amount">{f ? f(cur) : fmtK(cur)}</span>
                            </div>
                            <div className="billing-yoy-current billing-yoy-prior">
                              <span className="billing-yoy-year">{fmtMonth(momData.prev.month)}</span>
                              <span className="billing-yoy-amount">{f ? f(prev) : fmtK(prev)}</span>
                            </div>
                          </div>
                          {delta != null && (
                            <div className={`billing-yoy-delta ${up ? 'billing-yoy-delta--up' : 'billing-yoy-delta--down'}`}>
                              <FontAwesomeIcon icon={up ? faArrowTrendUp : faArrowTrendDown} />
                              {Math.abs(delta).toFixed(1)}% vs prior month
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Invoice Volume — Bar Chart ──────────────────────────────── */}
              {chartData.length > 0 && (
                <div className="billing-reports-section">
                  <h3 className="billing-reports-heading">Monthly Invoice Volume
                    <span className="billing-reports-sub">Last 12 months</span>
                  </h3>
                  <div className="billing-chart-wrap">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#94a3b8' }} width={60} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                        <Bar dataKey="invoiced" name="Invoiced" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="paid"     name="Paid"     fill="#10b981" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="credits"  name="Credits"  fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ── Outstanding Balance Trend — Area Chart ──────────────────── */}
              {chartData.length > 0 && (
                <div className="billing-reports-section">
                  <h3 className="billing-reports-heading">Outstanding Balance Trend
                    <span className="billing-reports-sub">Last 12 months</span>
                  </h3>
                  <div className="billing-chart-wrap">
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="outstandingGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#ef4444" stopOpacity={0.18} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#94a3b8' }} width={60} />
                        <Tooltip content={<ChartTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="outstanding"
                          name="Outstanding"
                          stroke="#ef4444"
                          strokeWidth={2}
                          fill="url(#outstandingGrad)"
                          dot={{ r: 3, fill: '#ef4444', strokeWidth: 0 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* ── Invoiced vs Paid — Area Chart ──────────────────────────── */}
              {chartData.length > 0 && (
                <div className="billing-reports-section">
                  <h3 className="billing-reports-heading">Invoiced vs Paid
                    <span className="billing-reports-sub">Last 12 months</span>
                  </h3>
                  <div className="billing-chart-wrap">
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="invoicedGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="paidGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="label" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11, fill: '#94a3b8' }} width={60} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                        <Area type="monotone" dataKey="invoiced" name="Invoiced"
                          stroke="#3b82f6" strokeWidth={2} fill="url(#invoicedGrad)" dot={false} />
                        <Area type="monotone" dataKey="paid" name="Paid"
                          stroke="#10b981" strokeWidth={2} fill="url(#paidGrad)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Invoice fetch loading overlay ────────────────────────────────────── */}
      {invPreviewLoading && (
        <div className="billing-inv-fetch-overlay">
          <div className="billing-spinner" />
          <span>Loading invoice…</span>
        </div>
      )}

      {/* ── Full invoice PDF preview — only mounted once data is complete ─────── */}
      {invoicePreview && !invPreviewLoading && (
        <InvoicePreview
          invoice={invoicePreview.invoice}
          lineItems={invoicePreview.lineItems}
          onClose={() => setInvoicePreview(null)}
        />
      )}

      {/* ── Credit Memo Detail Drawer ────────────────────────────────────────── */}
      {selectedCm && (
        <DetailDrawer title={`Credit Memo ${selectedCm.docNumber || selectedCm.id}`}
          onClose={closeDrawer} loading={detailLoading}>
          {detailData?.error && <p className="billing-drawer-err">Failed to load credit memo details.</p>}
          {detailData && !detailData.error && (() => {
            const cm = detailData.creditMemo;
            return (
              <>
                <div className="billing-drawer-meta">
                  {[
                    ['Credit Memo #', cm.docNumber],
                    ['Date', fmtDate(cm.txnDate)],
                    ['Total Credit', fmt(cm.totalAmt), 'billing-meta-amount'],
                    ['Remaining', fmt(cm.remainingCredit), `billing-meta-amount ${Number(cm.remainingCredit) > 0 ? 'billing-credit-avail' : ''}`],
                    ['Status', null, '', <CreditBadge memo={cm} />],
                    cm.billEmail && ['Email', cm.billEmail, 'billing-muted'],
                    cm.memo && ['Memo', cm.memo, 'billing-muted'],
                  ].filter(Boolean).map(([label, val, cls, node], i) => (
                    <div key={i} className="billing-meta-row">
                      <span className="billing-meta-label">{label}</span>
                      <span className={`billing-meta-value ${cls || ''}`}>{node || val}</span>
                    </div>
                  ))}
                </div>
                <h3 className="billing-drawer-section">Line Items</h3>
                <LineItemsTable items={detailData.lineItems} />
              </>
            );
          })()}
        </DetailDrawer>
      )}
    </div>
  );
}
